import React, { useState, useEffect, useRef, useContext, useReducer } from 'react';
import './mileage.css';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import ItemList from '../../components/sections/itemList';
import DateRangeSelector from '../../components/sections/dateRangeSelector';
import LargeCard from '../../components/sections/largeCard';
import Loader from '../../components/loader';
import { endOfDay, filterMileageReport, formatDate, formatDateRange, formatTime, parseDate, startOfDay, subtractDays } from '../../utils/generalFunctions';
import RetryReport from '../../components/retryReport';
import ChartLegends from '../../components/chartLegends';
import { DateContext } from '../../context/dateProvider';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin
);


const Mileage = ({ dataReport, devices, updateDevices, progress, error, fetchReport, userConstants, updateUserConstants, isRecoveredReports }) => {
    const { startDate, endDate, today,
        yesterday,
        startActualWeek,
        endActualWeek,
        startPreviousWeek,
        endPreviousWeek,
        startActualMonth,
        endActualMonth,
        startPreviousMonth,
        endPreviousMonth,
    } = useContext(DateContext);

    const [isLoading, setIsLoading] = useState(true);
    const [mileageData, setMileageData] = useState(isRecoveredReports && userConstants.reports && userConstants.reports.foundReports ? userConstants.reports.mileageData : []);
    const [totalData, setTotalData] = useState(isRecoveredReports && userConstants.reports && userConstants.reports.foundReports ? userConstants.reports.mileageTotalData : {});
    const [chartDateRange, setChartDateRange] = useState(0);
    const [showAllHigherTop, setShowAllHigherTop] = useState(false);
    const [showAllLesserTop, setShowAllLesserTop] = useState(false);
    const [dateHigherTopRange, setDateHigherTopRange] = useState(0);
    const [dateLesserTopRange, setDateLesserTopRange] = useState(0);
    const [displayedHigherData, setDisplayedHigherData] = useState([]);
    const [displayedLesserData, setDisplayedLesserData] = useState([]);
    const [showComparison, setShowComparison] = useState(false);
    const chartRef = useRef(null);
    const [activeTab, setActiveTab] = useState('bar');

    // const today = new Date(endDate);
    // const yesterday = subtractDays(1, today);
    // const startActualWeek = subtractDays(6, today);
    // const endActualWeek = today;
    // const startActualMonth = subtractDays(29, today);
    // const endActualMonth = today;
    // const startPreviousWeek = subtractDays(13, today);
    // const endPreviousWeek = subtractDays(7, today, true);
    // const startPreviousMonth = subtractDays(59, today);
    // const endPreviousMonth = subtractDays(30, today, true);

    useEffect(() => {
        if (!dataReport || dataReport.length === 0) return;
        setIsLoading(true);

        const transformedData = {
            objects: dataReport.report.sheets.map(sheet => {
                const obj = {
                    name: sheet.header,
                    summary: sheet.sections.flatMap(section => section.rows || []),
                    data: sheet.header !== "Período Resumen"
                        ? sheet.sections.flatMap(section =>
                            (section.data || []).map(dataItem => ({
                                date: dataItem.header,
                                total: dataItem.total
                            }))
                        )
                        : []
                };
                return obj;
            })
        };

        const processedData = filterMileageReport(dataReport, startPreviousMonth, endDate);

        setMileageData(processedData);

        // Aquí es donde acumulamos los datos por periodo
        let accumulatedData = {
            "Hoy": {},
            "Ayer": {},
            "Semana actual": {},
            "Semana previa": {},
            "Mes actual": {},
            "Mes previo": {}
        };

        // Inicializamos todos los vehículos con valores 0 en cada periodo
        transformedData.objects.forEach(obj => {
            ["Hoy", "Ayer", "Semana actual", "Semana previa", "Mes actual", "Mes previo"].forEach(period => {
                accumulatedData[period][obj.name] = { mileage: 0, time: 0 }; // Asignar 0 por defecto
            });
        });

        const addDataToPeriod = (period, carName, mileage, time) => {
            accumulatedData[period][carName].mileage += mileage;
            accumulatedData[period][carName].time += time;
        };

        // Procesamos los datos
        transformedData.objects.forEach(obj => {
            if (obj.data) {

                obj.data.forEach(entry => {
                    const entryDate = parseDate(entry.date);
                    const mileage = entry.total.length?.raw || 0;
                    const time = entry.total.time?.raw || 0;

                    if (entryDate.toDateString() === today.toDateString()) {
                        addDataToPeriod("Hoy", obj.name, mileage, time);
                    } else if (entryDate.toDateString() === yesterday.toDateString()) {
                        addDataToPeriod("Ayer", obj.name, mileage, time);
                    }

                    if (entryDate >= startActualWeek && entryDate <= endActualWeek) {
                        addDataToPeriod("Semana actual", obj.name, mileage, time);
                    }

                    if (entryDate >= startActualMonth && entryDate <= endActualMonth) {
                        addDataToPeriod("Mes actual", obj.name, mileage, time);
                    }

                    if (entryDate >= startPreviousWeek && entryDate <= endPreviousWeek) {
                        addDataToPeriod("Semana previa", obj.name, mileage, time);
                    }

                    if (entryDate >= startPreviousMonth && entryDate <= endPreviousMonth) {
                        addDataToPeriod("Mes previo", obj.name, mileage, time);
                    }
                });
            }
        });

        setTotalData(accumulatedData);

        updateUserConstants({ "reports.mileageData": processedData, "reports.mileageTotalData": accumulatedData });

        setIsLoading(false);
    }, [dataReport]);

    const updateChart = () => {
        if (mileageData.length === 0 || !chartRef.current || !chartRef.current.chartInstance) return;

        const updatedDatasets = devices.map(device => device.label)
            .flatMap((label, index) => {
                const mileageValues = mileageData.map(entry => entry[label]?.mileage || 0);
                const previousMileageValues = filteredPreviousData.map(entry => entry[label]?.mileage || 0);

                const isVisible = devices.find(device => device.label === label)?.visible ?? true;

                return [
                    {
                        label: `${label}`,
                        data: mileageValues,
                        fill: false,
                        borderColor: vibrantColors[index % vibrantColors.length],
                        backgroundColor: vibrantColors[index % vibrantColors.length],
                        tension: 0.1,
                        borderWidth: 2,
                        hidden: !isVisible // Ocultar según el estado visible del dispositivo
                    },
                    showComparison && {
                        label: `${label} (Anterior)`,
                        data: previousMileageValues,
                        fill: false,
                        borderColor: vibrantColors[index % vibrantColors.length],
                        backgroundColor: 'rgba(0, 0, 0, 0)',
                        borderDash: [5, 5],
                        tension: 0.1,
                        borderWidth: 2,
                        hidden: !isVisible // Ocultar también para comparación
                    }
                ].filter(Boolean);
            });

        chartRef.current.chartInstance.data.datasets = updatedDatasets;
        chartRef.current.chartInstance.update();
    };

    // Llamar a `updateChart` cuando se necesite actualizar la gráfica sin re-renderizar
    useEffect(() => {
        updateChart();
    }, [devices, mileageData, isRecoveredReports]);

    const calculateTotal = (mileageData) => {
        const totalData = {
            "Hoy": {},
            "Ayer": {},
            "Semana actual": {},
            "Semana previa": {},
            "Mes actual": {},
            "Mes previo": {}
        };

        const addDataToPeriod = (period, carName, mileage, time) => {
            totalData[period][carName].mileage += mileage;
            totalData[period][carName].time += time;
        };

        mileageData.forEach((entry) => {
            const entryDate = new Date(entry.date);

            Object.keys(entry).forEach((car) => {
                if (car === 'date') return;

                const { mileage, time } = entry[car];

                // Inicialización del vehículo si aún no existe en totalData
                Object.keys(totalData).forEach(period => {
                    if (!totalData[period][car]) {
                        totalData[period][car] = { mileage: 0, time: 0 };
                    }
                });

                if (entryDate.toDateString() === today.toDateString()) {
                    addDataToPeriod('Hoy', car, mileage, time);
                }

                if (entryDate.toDateString() === yesterday.toDateString()) {
                    addDataToPeriod('Ayer', car, mileage, time);
                }

                if (entryDate >= (startActualWeek) && entryDate <= (endActualWeek)) {
                    addDataToPeriod('Semana actual', car, mileage, time);
                }

                if (entryDate >= (startPreviousWeek) && entryDate <= (endPreviousWeek)) {
                    addDataToPeriod('Semana previa', car, mileage, time);
                }

                if (entryDate >= (startActualMonth) && entryDate <= (endActualMonth)) {
                    addDataToPeriod('Mes actual', car, mileage, time);
                }

                if (entryDate >= (startPreviousMonth) && entryDate <= (endPreviousMonth)) {
                    addDataToPeriod('Mes previo', car, mileage, time);
                }
            });
        });

        return totalData;
    };

    useEffect(() => {

        const newTotalData = calculateTotal(mileageData);
        setTotalData(newTotalData);

    }, [today, yesterday, startActualWeek, endActualWeek, startPreviousWeek, endPreviousWeek]);


    const filterDataByRange = (range, isPrevious = false) => {
        const filteredData = [];

        let startDate2, endDate2;

        // Elegir fechas según el rango y si es el periodo anterior o actual
        switch (range) {
            case 0:
                startDate2 = isPrevious ? startOfDay(yesterday) : startOfDay(today);
                endDate2 = isPrevious ? endOfDay(yesterday) : endOfDay(today);
                break;
            case 1:
                startDate2 = isPrevious ? startOfDay(startPreviousWeek) : startOfDay(startActualWeek);
                endDate2 = isPrevious ? endOfDay(endPreviousWeek) : startOfDay(endActualWeek);
                break;
            case 2:
                startDate2 = isPrevious ? startOfDay(startPreviousMonth) : startOfDay(startActualMonth);
                endDate2 = isPrevious ? endOfDay(endPreviousMonth) : endOfDay(endActualMonth);
                break;
            default:
                startDate2 = startOfDay(new Date(startDate));
                endDate2 = endOfDay(today);
        }

        // Filtrar los datos de acuerdo al rango seleccionado
        mileageData.forEach(item => {
            if (new Date(item.date) >= startDate2 && new Date(item.date) <= endDate2) {
                filteredData.push(item);
            }
        });

        return filteredData;
    };

    // Uso de la nueva función generalizada
    const filteredData = filterDataByRange(chartDateRange);               // Para el rango actual
    const filteredPreviousData = filterDataByRange(chartDateRange, true); // Para el rango anterior


    // Obtener solo las fechas con al menos un vehículo con kilometraje > 0
    const validDates = filteredData.filter(day =>
        devices.some(device => device.visible && day[device.label]?.mileage > 0)
    );

    const labels = validDates.map(day => formatDate(day.date));

    const vibrantColors = [];
    devices.forEach(item => {
        vibrantColors.push(item.color);
    });

    const datasets = devices.map(device => device.label)
        .flatMap((label, index) => {
            const currentData = filteredData
                .filter(day => devices.some(d => d.visible && day[d.label]?.mileage > 0))
                .map(day => day[label]?.mileage || 0);

            const previousData = filteredPreviousData
                .filter(day => devices.some(d => d.visible && day[d.label]?.mileage > 0))
                .map(day => day[label]?.mileage || 0);

            const isVisible = devices.find(device => device.label === label)?.visible ?? true;

            return [
                {
                    label: `${label}`,
                    data: currentData,
                    fill: false,
                    borderColor: vibrantColors[index % vibrantColors.length],
                    backgroundColor: vibrantColors[index % vibrantColors.length],
                    tension: 0.1,
                    borderWidth: 2,
                    hidden: !isVisible // Ocultar según el estado visible del dispositivo
                },
                showComparison && {
                    label: `${label} (Anterior)`,
                    data: previousData,
                    fill: false,
                    borderColor: vibrantColors[index % vibrantColors.length],
                    backgroundColor: 'rgba(0, 0, 0, 0)',
                    borderDash: [5, 5],
                    tension: 0.1,
                    borderWidth: 2,
                    hidden: !isVisible // Ocultar también para comparación
                }
            ].filter(Boolean); // Filtrar elementos nulos/undefined
        }) || [];

    const data = {
        labels,
        datasets,
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: false,
                    text: "Fecha",
                    font: {
                        size: 16,
                        weight: 'bold',
                        lineHeight: 1.2,
                    },
                }
            },
            y: {
                display: true,
                title: {
                    display: true,
                    text: "Kilometraje (km)",
                    font: {
                        size: 16,
                        weight: 'bold',
                        lineHeight: 1.2,
                    },
                }
            }
        },
        plugins: {
            zoom: {
                zoom: {
                    wheel: {
                        enabled: chartDateRange !== 0, // Habilitar zoom con la rueda del ratón
                        threshold: 5,
                        modifierKey: 'ctrl'
                    },
                    pinch: {
                        enabled: chartDateRange !== 0, // Habilitar zoom con gestos táctiles
                        threshold: 2,
                    },
                    mode: 'x', // Hacer zoom solo en el eje X
                },
                pan: {
                    enabled: true, // Habilitar paneo (desplazamiento)
                    mode: 'x', // Permitir desplazamiento solo en el eje X
                    modifierKey: 'ctrl'
                },
            },
            legend: {
                display: false,
                position: 'bottom',
                onHover: (event) => {
                    event.native.target.style.cursor = 'pointer';
                },
                onLeave: (event) => {
                    event.native.target.style.cursor = 'default';
                },
                onClick: (e, legendItem, legend) => {
                    const chart = legend.chart;
                    const datasetIndex = legendItem.datasetIndex;
                    const dataset = chart.data.datasets[datasetIndex];
                    const deviceName = dataset.label.replace(' (Anterior)', '');

                    // Alternar visibilidad del dataset
                    const isHidden = !dataset.hidden;
                    dataset.hidden = isHidden;

                    // Actualiza el estado de `devices` para reflejar el cambio
                    const updatedDevices = devices.map(device =>
                        device.label === deviceName ? { ...device, visible: !isHidden } : device
                    );

                    updateDevices(updatedDevices);

                    // Si `showComparison` está activado, también alterna el dataset de comparación
                    if (showComparison) {
                        const comparisonDataset = chart.data.datasets[datasetIndex + 1];
                        if (comparisonDataset && comparisonDataset.label.includes('(Anterior)')) {
                            comparisonDataset.hidden = isHidden;
                        }
                    }

                    chart.update(); // Actualiza la gráfica
                },

                labels: {
                    usePointStyle: true,
                    padding: 15,
                    pointStyle: 'circle',
                    boxWidth: 8,        // Ajusta el ancho de la caja del punto
                    boxHeight: 8,
                    font: {
                        size: 14
                    },
                    filter: (legendItem) => !legendItem.text.includes('Anterior'), // Filtra para mostrar solo "Actual"
                },
            },
            title: {
                display: false,
                text: 'Kilometraje de Flota',
            },
            tooltip: {
                callbacks: {
                    title: function (tooltipItems) {
                        // Solo tomamos el primer elemento del array tooltipItems
                        const tooltipItem = tooltipItems[0];
                        const index = tooltipItem.dataIndex;
                        const isPreviousPeriod = tooltipItem.dataset.label.includes('(Anterior)');
                        const previousDates = filteredPreviousData.map(item => formatDate(item.date));

                        // Obtener la fecha correspondiente
                        const dateLabel = isPreviousPeriod
                            ? previousDates[index] + ' (Previo)' // Si es el periodo anterior, usa `previousDates`
                            : labels[index];       // Si es el actual, usa `labels`

                        return dateLabel;
                    },
                    label: function (tooltipItem) {
                        const dataset = tooltipItem.dataset.label.replace(/\s*\(Actual\)|\s*\(Anterior\)/g, '');
                        const value = tooltipItem.raw;

                        setValuesPeriod(dataset);

                        // Formatea el texto del tooltip
                        return `${dataset}: ${value} km`;
                    }
                }
            }
        },
    };

    const handleCheckboxChange = () => {
        setShowComparison(!showComparison); // Alternar el estado
    };

    const setValuesPeriod = (dataset) => {
        // Obtener la opción seleccionada del selector
        const selectedOption = chartDateRange
        let currentPeriodMileage, previousPeriodMileage, currentPeriodDates, previousPeriodDates;
        // Actualizar los spans con el kilometraje total y las fechas de los periodos
        if (selectedOption === 0) {
            currentPeriodMileage = calculatePeriodTotalData('Hoy', dataset, 'mileage');
            previousPeriodMileage = calculatePeriodTotalData('Ayer', dataset, 'mileage');

            currentPeriodDates = { startDate: startOfDay(new Date(today)), endDate: today };
            previousPeriodDates = { startDate: yesterday, endDate: subtractDays(1, today, true) };
        } else if (selectedOption === 1) {
            currentPeriodMileage = calculatePeriodTotalData('Semana actual', dataset, 'mileage');
            previousPeriodMileage = calculatePeriodTotalData('Semana previa', dataset, 'mileage');

            currentPeriodDates = { startDate: startActualWeek, endDate: endActualWeek };
            previousPeriodDates = { startDate: startPreviousWeek, endDate: endPreviousWeek };
        } else if (selectedOption === 2) {
            currentPeriodMileage = calculatePeriodTotalData('Mes actual', dataset, 'mileage');
            previousPeriodMileage = calculatePeriodTotalData('Mes previo', dataset, 'mileage');

            currentPeriodDates = { startDate: startActualMonth, endDate: endActualMonth };
            previousPeriodDates = { startDate: startPreviousMonth, endDate: endPreviousMonth };
        }

        const currentPeriodElem = document.getElementById('current-period-mileage');
        const previousPeriodElem = document.getElementById('previous-period-mileage');

        currentPeriodElem.innerText = `${currentPeriodMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`;
        previousPeriodElem.innerText = `${previousPeriodMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`;

        // Remover clases previas
        currentPeriodElem.classList.remove('high', 'normal', 'low');
        previousPeriodElem.classList.remove('high', 'normal', 'low');

        // Cambiar el className según la comparación
        if (currentPeriodMileage > previousPeriodMileage) {
            currentPeriodElem.classList.add('high');
            previousPeriodElem.classList.add('low');
        } else if (currentPeriodMileage < previousPeriodMileage) {
            currentPeriodElem.classList.add('low');
            previousPeriodElem.classList.add('high');
        } else {
            // Si son iguales, ambos valores tendrán la clase 'yellow'
            currentPeriodElem.classList.add('normal');
            previousPeriodElem.classList.add('normal');
        }

        // Formatear las fechas en dd/mm/aaaa - dd/mm/aaaa
        if (selectedOption === 0) {
            document.getElementById('current-period-date-mileage').innerText = `(${formatDate(currentPeriodDates.startDate)})`;
            document.getElementById('previous-period-date-mileage').innerText = `(${formatDate(previousPeriodDates.startDate)})`;
        } else {
            document.getElementById('current-period-date-mileage').innerText = `(${formatDate(currentPeriodDates.startDate)} - ${formatDate(currentPeriodDates.endDate)})`;
            document.getElementById('previous-period-date-mileage').innerText = `(${formatDate(previousPeriodDates.startDate)} - ${formatDate(previousPeriodDates.endDate)})`;
        }
    };

    const calculatePeriodTotalData = (period, car, dataKey) => {
        const periodData = totalData[period];

        if (periodData && periodData[car] && periodData[car][dataKey]) {
            return periodData[car][dataKey]; // Retornar el valor correcto (mileage, time, etc.)
        }

        return 0; // Si no hay datos, retornar 0
    };

    // Función auxiliar para obtener el kilometraje de una fecha específica
    const getMileageForDate = (car, date) => {
        const data = mileageData.find(item => new Date(item.date) === (date));
        return data && data[car] ? data[car].mileage || 0 : 0;
    };

    // Función auxiliar para obtener el kilometraje acumulado en un rango de fechas
    const getMileageForchartDateRange = (car, startDate, endDate) => {
        let totalMileage = 0;
        let currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            totalMileage += getMileageForDate(car, currentDate);
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return totalMileage;
    };


    const resetZoom = () => {
        if (chartRef.current) {
            chartRef.current.resetZoom(); // Usar la referencia para resetear el zoom
        }
    };

    const getMileageBychartDateRange = (range) => {
        let periodCurrentData = {}, periodPreviousData = {};

        switch (range) {
            case 0:
                periodCurrentData = totalData['Hoy'] || {};
                periodPreviousData = totalData['Ayer'] || {};
                break;
            case 1:
                periodCurrentData = totalData['Semana actual'] || {};
                periodPreviousData = totalData['Semana previa'] || {};
                break;
            case 2:
                periodCurrentData = totalData['Mes actual'] || {};
                periodPreviousData = totalData['Mes previo'] || {};
                break;
            default:
                return [];
        }

        // Filtramos dispositivos visibles
        const visibleDevices = devices.filter(device => device.visible);

        // Combinar la data del periodo actual y previo
        const combinedData = Object.keys({ ...periodCurrentData, ...periodPreviousData })
            .filter(car => visibleDevices.find(device => device.label === car))  // Filtra solo los visibles
            .map((car) => {
                const current = periodCurrentData[car] || { mileage: 0, time: 0 };
                const previous = periodPreviousData[car] || { mileage: 0, time: 0 };

                return {
                    car, // Nombre del vehículo
                    currentMileage: current.mileage, // Kilometraje actual
                    currentTime: current.time, // Tiempo actual
                    previousMileage: previous.mileage, // Kilometraje previo
                    previousTime: previous.time // Tiempo previo
                };
            });

        // Ordenar por el kilometraje actual
        return combinedData;
    };

    useEffect(() => {
        const sortedHigherMileageData = getMileageBychartDateRange(dateHigherTopRange)
            .sort((a, b) => {
                if (b.currentMileage === a.currentMileage) {
                    return b.previousTime - a.previousTime; // Comparar por tiempo previo si el kilometraje es igual
                }
                return b.currentMileage - a.currentMileage; // Comparar por kilometraje (descendente)
            });
        setDisplayedHigherData(showAllHigherTop ? sortedHigherMileageData : sortedHigherMileageData.slice(0, 5));
    }, [devices, totalData, dateHigherTopRange, showAllHigherTop]);

    useEffect(() => {
        const sortedLesserMileageData = getMileageBychartDateRange(dateLesserTopRange)
            .sort((a, b) => {
                if (a.currentMileage === b.currentMileage) {
                    if (a.previousTime === b.previousTime) {
                        // Colocar el primer elemento como prioridad si todo lo anterior es igual
                        return -1; // Mantener el orden original si el kilometraje y tiempo son iguales
                    }
                    return a.previousTime - b.previousTime; // Ordenar por tiempo si el kilometraje es igual
                }
                return a.currentMileage - b.currentMileage; // Ordenar por kilometraje de forma ascendente
            });
        setDisplayedLesserData(showAllLesserTop ? sortedLesserMileageData : sortedLesserMileageData.slice(0, 5));
    }, [devices, totalData, dateLesserTopRange, showAllLesserTop]);


    const totalItems = displayedHigherData.length;

    // Calcular el ancho basado en la cantidad de dígitos
    const indexWidth = totalItems < 10 ? '2ch' : totalItems < 100 ? '3ch' : '4ch';

    // Función para alternar entre "Ver todos" y "Ver menos"
    const handleShowAllHigherTopToggle = () => {
        setShowAllHigherTop(!showAllHigherTop);
    };

    const handleShowAllLesserTopToggle = () => {
        setShowAllLesserTop(!showAllLesserTop);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const showZoomMessage = useRef(false);
    const [, forceRender] = useReducer(x => x + 1, 0);
    const zoomTimeout = useRef(null);


    const handleWheel = (e) => {
        if (chartDateRange > 0 && !e.ctrlKey) {
          if (!showZoomMessage.current) {
            showZoomMessage.current = true;
            forceRender();
      
            if (zoomTimeout.current) clearTimeout(zoomTimeout.current);
            zoomTimeout.current = setTimeout(() => {
              showZoomMessage.current = false;
              forceRender();
            }, 3000);
          }
        }
      };      

    const renderContent = () => {
        switch (activeTab) {
            case 'bar':
                return <>
                    <div className='chart-container' onWheel={handleWheel} style={{ flex: 1, position: 'relative' }}>
                        <Bar ref={chartRef} data={data} options={options} />
                        <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
                            Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
                        </div>
                    </div>
                    <ChartLegends devices={devices} updateDevices={updateDevices} id={'mileage-bar-'} />
                </>
            case 'line':
                return <>
                    <div className='chart-container' style={{ flex: 1, position: 'relative' }}>
                        <Line ref={chartRef} data={data} options={options} />
                        <div className="zoom-message" style={{ display: showZoomMessage.current ? 'flex' : 'none' }}>
                            Mantén presionada la tecla Ctrl y usa la rueda del ratón para hacer zoom.
                        </div>
                    </div>
                    <ChartLegends devices={devices} updateDevices={updateDevices} id={'mileage-line-'} />
                </>
            case 'table':
                return renderTable();
            default:
                return null;
        }
    };

    const renderTable = () => {
        const visibleDevices = devices.filter(device => device.visible).map(device => device.label);

        const summary = {};

        const actualData = filterDataByRange(chartDateRange); // Datos del periodo actual
        const previousData = filterDataByRange(chartDateRange, true); // Datos del periodo anterior

        const dates = [];
        const previousDates = [];

        // Filtrar los datos del periodo actual para llenar el resumen solo con los dispositivos visibles
        actualData.forEach(entry => {
            const { date, ...cars } = entry;
            if (!dates.includes(date)) dates.push(date); // Agregar fechas del periodo actual sin duplicarlas

            visibleDevices.forEach(carName => {
                if (cars[carName]) {
                    if (!summary[carName]) {
                        summary[carName] = { actual: [], previous: [], totalActualMileage: 0, totalActualTime: 0, totalPreviousMileage: 0, totalPreviousTime: 0 };
                    }
                    const carData = cars[carName];
                    summary[carName].actual.push({
                        mileage: carData.mileage,
                        time: carData.time
                    });
                    summary[carName].totalActualMileage += carData.mileage;
                    summary[carName].totalActualTime += carData.time;
                }
            });
        });

        // Filtrar los datos del periodo anterior para llenar el resumen solo con los dispositivos visibles
        previousData.forEach(entry => {
            const { date, ...cars } = entry;
            if (!previousDates.includes(date)) previousDates.push(date); // Agregar fechas del periodo anterior sin duplicarlas

            visibleDevices.forEach(carName => {
                if (cars[carName]) {
                    if (!summary[carName]) {
                        summary[carName] = { actual: [], previous: [], totalActualMileage: 0, totalActualTime: 0, totalPreviousMileage: 0, totalPreviousTime: 0 };
                    }
                    const carData = cars[carName];
                    summary[carName].previous.push({
                        mileage: carData.mileage,
                        time: carData.time
                    });
                    summary[carName].totalPreviousMileage += carData.mileage;
                    summary[carName].totalPreviousTime += carData.time;
                }
            });
        });
        console.log(previousData)

        return (
            <div className='table_container'>
                <table>
                    <thead>
                        <tr>
                            <th rowSpan={2}>Nombre del Carro</th>
                            <th colSpan={2}>Total Periodo Actual</th>
                            {showComparison && <th colSpan={2} className='previous_data'>Total Periodo Anterior</th>}
                            {dates.map((date, index) => (
                                <React.Fragment key={`period-${formatDate(date)}-${index}`}>
                                    <th colSpan={2}>{formatDate(date)}</th>
                                    {showComparison && (
                                        <th colSpan={2} className='previous_data'>{formatDate(previousDates[index]) || 'Fecha Anterior'}</th>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>
                        <tr>
                            <th>Kilometraje</th>
                            <th>Tiempo</th>
                            {showComparison && (
                                <>
                                    <th className='previous_data'>Kilometraje</th>
                                    <th className='previous_data'>Tiempo</th>
                                </>
                            )}
                            {dates.map((date, index) => (
                                <React.Fragment key={`subheader-${formatDate(date)}-${index}`}>
                                    <th key={`current-km-${index}`}>Kilometraje</th>
                                    <th key={`current-time-${index}`}>Tiempo</th>
                                    {showComparison && (
                                        <>
                                            <th className='previous_data' key={`previous-km-${index}`}>Kilometraje</th>
                                            <th className='previous_data' key={`previous-time-${index}`}>Tiempo</th>
                                        </>
                                    )}
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {Object.entries(summary).map(([carName, stats], index) => (
                            <tr onMouseOver={() => setValuesPeriod(carName)} key={`car-row-${index}`}>
                                <td>{carName}</td>
                                <td className="total-cell" key={`total-currentMileage-${carName}`}>
                                    {stats.totalActualMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
                                </td>
                                <td className="total-cell" key={`total-currentTime-${carName}`}>
                                    {formatTime(stats.totalActualTime)}
                                </td>
                                {showComparison && (
                                    <>
                                        <td className="total-cell" key={`total-previousMileage-${carName}`}>
                                            {stats.totalPreviousMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
                                        </td>
                                        <td className="total-cell" key={`total-previousTime-${carName}`}>
                                            {formatTime(stats.totalPreviousTime)}
                                        </td>
                                    </>
                                )}
                                {dates.map((date, dateIndex) => {
                                    const actualEntry = stats.actual[dateIndex] || { mileage: 0, time: 0 };
                                    const previousEntry = stats.previous[dateIndex] || { mileage: 0, time: 0 };

                                    return (
                                        <React.Fragment key={`data-${carName}-${formatDate(date)}-${dateIndex}`}>
                                            <td key={`currentMileage-${carName}-${dateIndex}`}>
                                                {actualEntry.mileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
                                            </td>
                                            <td key={`currentTime-${carName}-${dateIndex}`}>
                                                {formatTime(actualEntry.time)}
                                            </td>
                                            {showComparison && (
                                                <>
                                                    <td key={`previousMileage-${carName}-${dateIndex}`}>
                                                        {previousEntry.mileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km
                                                    </td>
                                                    <td key={`previousTime-${carName}-${dateIndex}`}>
                                                        {formatTime(previousEntry.time)}
                                                    </td>
                                                </>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const [cardsReady, setCardsReady] = useState([]);

    useEffect(() => {
        const totalCards = 3; // Cambiar este número según la cantidad de tarjetas
        if (progress === 100) {
            // Generar tiempos aleatorios para cada tarjeta
            const randomDelays = Array.from({ length: totalCards }, () => Math.random() * 2000 + 1000); // 1-3 segundos
            const timeouts = randomDelays.map((delay, index) => {
                return setTimeout(() => {
                    setCardsReady((prev) => {
                        const updated = [...prev];
                        updated[index] = true;
                        return updated;
                    });
                }, delay);
            });

            // Limpiar los timeouts al desmontar
            return () => timeouts.forEach((timeout) => clearTimeout(timeout));
        } else {
            setCardsReady(new Array(totalCards).fill(false));
        }
    }, [progress]);

// console.log(date)
    return (
        <div className='section_grid_container'>
            <LargeCard
                variant='mileage'
                dateRange={chartDateRange}
                setDateRange={setChartDateRange}
                showComparison={showComparison}
                setShowChartComparison={setShowComparison}
                handleCheckboxChange={handleCheckboxChange}
                handleTabChange={handleTabChange}
                activeTab={activeTab}
                renderContent={renderContent}
                resetZoom={resetZoom}
                progress={progress}
                isReady={!cardsReady[0]}
                error={error}
                fetchReport={fetchReport}
                sectionNum={0}
            />
            <div className='dashboard_card card_top card_short'>

                {
                    error ?
                        <RetryReport fetchReport={fetchReport} sectionNum={0} />
                        :
                        progress < 100 || !cardsReady[1] ? (
                            <Loader progress={progress} />
                        ) : (
                            <>
                                <div className='card_header_flex'>
                                    <div className='dashboard_card_header_container'>
                                        <span className='dashboard_card_title'>{showAllHigherTop ? 'Todos los' : 'Top 5'} vehículos con mayor kilometraje</span>
                                        <button className='dashboard_card_view_more' onClick={handleShowAllHigherTopToggle}>
                                            {showAllHigherTop ? 'Ver menos' : 'Ver todos'}
                                        </button>
                                    </div>
                                    <div className='card_config_container'>
                                        <DateRangeSelector dateRange={dateHigherTopRange} setDateRange={setDateHigherTopRange} />
                                    </div>
                                </div>
                                <div className='dashboard_card_content_container'>
                                    <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                        <div className='top_items_header_container'>
                                            <span>Periodo</span>
                                            <span>Actual<br/><span>({formatDateRange(dateHigherTopRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                            <span>Previo<br/><span>({formatDateRange(dateHigherTopRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                        </div>
                                        {
                                            displayedHigherData.length === 0 ?
                                                <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                                :
                                                displayedHigherData.map((item, index) => (
                                                    <ItemList
                                                        key={index}
                                                        index={index + 1}
                                                        type={'mileage'}
                                                        name={item.car} // Nombre del vehículo
                                                        data={{
                                                            currentMileage: `${item.currentMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, // Kilometraje actual formateado
                                                            currentTime: item.currentTime, // Tiempo actual formateado
                                                            previousMileage: `${item.previousMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, // Kilometraje previo formateado
                                                            previousTime: item.previousTime // Tiempo previo formateado
                                                        }}

                                                    />
                                                ))
                                        }
                                    </div>
                                </div>
                            </>
                        )}
            </div>
            <div className='dashboard_card card_top card_short'>
                {error ?
                    <RetryReport fetchReport={fetchReport} sectionNum={0} />
                    :
                    progress < 100 || !cardsReady[2] ? (
                        <Loader progress={progress} />
                    ) : (
                        <>
                            <div className='card_header_flex'>
                                <div className='dashboard_card_header_container'>
                                    <span className='dashboard_card_title'>{showAllLesserTop ? 'Todos los' : 'Top 5'} vehículos con menor kilometraje</span>
                                    <button className='dashboard_card_view_more' onClick={handleShowAllLesserTopToggle}>
                                        {showAllLesserTop ? 'Ver menos' : 'Ver todos'}
                                    </button>
                                </div>
                                <div className='card_config_container'>
                                    <DateRangeSelector dateRange={dateLesserTopRange} setDateRange={setDateLesserTopRange} />
                                </div>
                            </div>
                            <div className='dashboard_card_content_container'>
                                <div className='top_items_container' style={{ '--item-index-width': indexWidth }}>
                                    <div className='top_items_header_container'>
                                        <span>Periodo</span>
                                        <span>Actual<br/><span>({formatDateRange(dateLesserTopRange, today, startActualWeek, endActualWeek, startActualMonth, endActualMonth)})</span></span>
                                        <span>Previo<br/><span>({formatDateRange(dateLesserTopRange, yesterday, startPreviousWeek, endPreviousWeek, startPreviousMonth, endPreviousMonth)})</span></span>
                                    </div>
                                    {
                                        displayedLesserData.length === 0 ?
                                            <span className='text_no_vehicles'>Ningún vehículo seleccionado</span>
                                            :
                                            displayedLesserData.map((item, index) => (
                                                <ItemList
                                                    key={index}
                                                    index={index + 1}
                                                    type={'mileage'}
                                                    name={item.car} // Nombre del vehículo
                                                    data={{
                                                        currentMileage: `${item.currentMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, // Kilometraje actual formateado
                                                        currentTime: item.currentTime, // Tiempo actual formateado
                                                        previousMileage: `${item.previousMileage.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, // Kilometraje previo formateado
                                                        previousTime: item.previousTime // Tiempo previo formateado
                                                    }}
                                                />
                                            ))
                                    }
                                </div>
                            </div>
                        </>
                    )}
            </div>
        </div>
    );
};

export default Mileage;
